import { Navigate, Outlet } from "react-router-dom";
import { useAppSelector } from "../app/hooks";
import { ADMIN_ROLES, ELEVATED_ROLES } from "../features/auth/roles";

/**
 * A route element that admits only the roles it names.
 *
 * WHY THIS EXISTS
 * There was no role gating on any route. `ProtectedRoute` checks that a token
 * exists and has not expired -- authentication -- and `withPageGuard` is an
 * error boundary. Neither looks at who you are.
 *
 * So a student who typed `/manage/users` stayed there and was shown the admin
 * page's heading, its chrome, and an empty table, with nothing saying why. The
 * DATA was never at risk: every endpoint behind those screens answers a student
 * with 403, which was verified against the running server. This is the missing
 * half -- being told, instead of being shown a screen that silently does
 * nothing.
 *
 * WHY IT REDIRECTS RATHER THAN RENDERING A MESSAGE
 * The routes it guards are ones a student has no business on and no link to;
 * they arrive by typing a URL or following a stale bookmark. Sending them to
 * their own dashboard puts them somewhere they can act, which an "access
 * denied" page does not. `replace` so Back does not bounce them straight into
 * the wall again.
 *
 * WHY IT IS NOT A SUBSTITUTE FOR THE SERVER'S CHECKS
 * It is a UI convenience, and it is defeated by anyone who opens devtools. The
 * authorisation that matters is the 403 on each endpoint. Anything added here
 * must be gated there too.
 */
export const RequireRole = ({ allow }: { allow: readonly string[] }) => {
    const role = useAppSelector((state) => state.auth.profile?.role);

    // No profile yet: the token is present (ProtectedRoute ran first) but the
    // claims have not been read. Rendering the route would flash admin chrome at
    // a student for a frame; redirecting would throw an admin off their own
    // page on a refresh. Waiting is the only option that is wrong for nobody.
    if (role == null) {
        return null;
    }

    if (!allow.includes(role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Outlet />;
};

/** The staff AREA (/manage/*, /admin/*): admins plus the read-only power user,
 *  matching the backend's ELEVATED_ROLES. A teacher is deliberately not in it
 *  yet - those screens list the whole organisation's people. */
export const STAFF_ROLES = ELEVATED_ROLES;

/** The narrower set, for screens whose endpoints admit only admins. */
export { ADMIN_ROLES };
